// @flow
import React from 'react'
import Button from '@material-ui/core/Button'
import Snackbar from '@material-ui/core/Snackbar'
import OlEvent from 'ol/events/Event'
import OlFeature from 'ol/Feature'
import OlVectorLayer from 'ol/layer/Vector'
import OlVectorSource from 'ol/source/Vector'
import Circle from 'ol/style/Circle'
import Fill from 'ol/style/Fill'
import Stroke from 'ol/style/Stroke'
import Text from 'ol/style/Text'
import { olUtils, styles } from 'catena/er2_ui'
import Er2Map from 'catena/er2_map/src/map'
import type { FicusDataType } from '../../reducers/select_framework_bar'
import type { RefugeeModelType } from '../../reducers/refugee_model'

type Props = {
    ficusData: FicusDataType,
    er2Map: Er2Map,
    generateGridFromPoly: Function,
    hasAnalysisPanel: boolean,
    hasHeader: boolean,
    onChangeProps: Function,
    onDisplayStatistics: Function,
    refugeeModel: RefugeeModelType,
    updateRefugeeLayer: Function,
    theme: styles,
    tocVisible: boolean,
    updateRefugeeLayer: Function,
}

type State = {
    affected: OlFeature,
    combat: OlFeature,
    // Zone that is currently being drawn
    currentZone: string,
    drawPromptRendered: boolean,
    drawType?: 'poly',
}

const getZoneText = zone => (zone === 'affected' ? 'Collateral Injury Area #1' : 'Direct Injury Area #1')
const affectedBrushColor = 'rgba(0, 255, 0, 0.5)'
const combatBrushColor = 'rgba(255, 0, 0, 0.5)'

class RefugeeModel extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props)
        this.state = {
            drawPromptRendered: true,
        }
    }

    componentDidMount() {
        this.vectorSource = new OlVectorSource({
            projection: 'EPSG:3857',
        })
        this.vectorLayer = new OlVectorLayer({
            source: this.vectorSource,
            style: (feat, res) => this.getStyle(feat, res),
            zIndex: 1,
        })

        this.props.er2Map.olmap.addLayer(this.vectorLayer)
        this.props.er2Map.olmap.render()
    }

    componentWillUnmount() {
        if (this.drawInteraction) {
            this.props.er2Map.olmap.removeInteraction(this.drawInteraction)
        }
        this.props.er2Map.olmap.removeLayer(this.vectorLayer)
    }

    getStyle(feature) {
        const label = getZoneText(feature.get('zone'))
        let textStyle
        if (label) {
            textStyle = new Text({
                fill: new Fill(olUtils.styles.text.fill),
                font: '16px "Roboto Condensed", sans-serif',
                placement: 'point',
                stroke: new Stroke(olUtils.styles.text.stroke),
                text: label,
            })
        }

        const fill = new Fill({
            color: feature.get('zone') === 'affected' ? affectedBrushColor : combatBrushColor,
            opacity: 0.6,
        })

        return olUtils.getOlStyles({
            fill,
            image: new Circle({
                radius: 7,
                fill: new Fill({ color: 'black' }),
                stroke: new Stroke({
                    color: [255, 0, 0], width: 2,
                }),
            }),
            text: textStyle,
        })
    }

    // Shapes that don't require buffers can be handled here.
    onDrawShape(evt: OlEvent, zoneType) {
        evt.feature.set('zone', zoneType)
        // Remove previous features
        this.vectorSource.getFeatures().forEach((f) => {
            if (f.get('zone') === zoneType) {
                this.vectorSource.removeFeature(f)
            }
        })
        this.vectorSource.addFeature(evt.feature)
        // Update zone feature
        this.setState({ [zoneType]: olUtils.writeFeatures(evt.feature), currentZone: '', drawType: '' })
        this.props.er2Map.olmap.removeInteraction(this.drawInteraction)
    }

    onDisplayStatistics = () => {
        this.props.onDisplayStatistics()
    }

    onSelectArea = () => {
        if (!this.props.refugeeModel.grid) {
            this.props.generateGridFromPoly(this.state.affected, this.state.combat)
            this.vectorSource.clear()
        } else {
            // Toggle display of grid
            const layer = this.props.refugeeModel.grid
            this.props.onChangeProps(layer.mapsource, layer.name, { visible: !layer.visible })
            // Since the layer object is different from the er2_map version, I need to update the
            //   metadata locally.
            layer.visible = !layer.visible
        }
    }

    setDrawType(drawType, zoneType) {
        if (drawType === 'poly') {
            this.drawInteraction = olUtils.makeDrawPolygonInteraction()
            this.drawInteraction.on('drawend', evt => this.onDrawShape(evt, zoneType))
            this.props.er2Map.olmap.addInteraction(this.drawInteraction)
        }
    }

    setDrawTypeAffected = () => {
        this.setDrawType('poly', 'affected')
        this.setState({ currentZone: 'affected', drawType: 'poly', drawPromptRendered: false })
        this.props.updateRefugeeLayer()
    }

    setDrawTypeCombat = () => {
        this.setDrawType('poly', 'combat')
        this.setState({ currentZone: 'combat', drawType: 'poly', drawPromptRendered: false })
    }

    renderNotifications() {
        function getDrawPromptMessage(msgType, zone) {
            switch (msgType) {
            case 'poly':
                return `Click to begin drawing the ${getZoneText(zone)} polygon on the map`
            default:
                return 'Unknown message type'
            }
        }

        const drawMessage = this.state.drawType ? getDrawPromptMessage(this.state.drawType, this.state.currentZone) : ''
        return (<div>
            <Snackbar
                open={this.state.drawPromptRendered === false}
                autoHideDuration={6000}
                onClose={() => this.setState({ drawPromptRendered: true })}
                message={<span id="message-id">{drawMessage}</span>}
            />
        </div>)
    }

    render() {
        const button = {
            margin: '6px 20px',
        }

        const refugeeLayer = this.props.refugeeModel.grid
        const refugeeLayerIsVisible = refugeeLayer && refugeeLayer.visible
        const refugeeLayerButtonStyle = { ...button }
        if (refugeeLayerIsVisible) refugeeLayerButtonStyle.border = '1px solid blue'

        return (
            <div className={this.props.theme.controlBar}>
                <Button
                    style={button}
                    classes={{
                        root: styles.buttonSubmit,
                    }}
                    onClick={this.setDrawTypeCombat}
                    variant={this.state.currentZone === 'combat' ? 'outlined' : 'contained'}
                >Map Direct Injury Areas</Button>
                <Button
                    style={button}
                    classes={{
                        root: styles.buttonSubmit,
                    }}
                    onClick={this.setDrawTypeAffected}
                    variant={this.state.currentZone === 'affected' ? 'outlined' : 'contained'}
                >Map Collateral Injury Areas</Button>
                <Button
                    style={refugeeLayerButtonStyle}
                    classes={{
                        root: styles.buttonSubmit,
                    }}
                    disabled={!refugeeLayer && !(this.state.affected && this.state.combat)}
                    onClick={this.onSelectArea}
                    variant={'contained'}
                >{refugeeLayerIsVisible ? 'Hide' : 'Display'} All Injury Areas</Button>
                <Button
                    style={button}
                    classes={{
                        root: styles.buttonSubmit,
                    }}
                >
                    <span>Modify Cohort Analysis for Injury Areas</span>
                </Button>
                <Button
                    style={button}
                    classes={{
                        root: styles.buttonSubmit,
                    }}
                >
                    <span>Compute Refugee Model</span>
                </Button>
                <Button
                    style={button}
                    classes={{
                        root: styles.buttonSubmit,
                    }}
                    onClick={this.onDisplayStatistics}
                    variant={'contained'}
                >
                    <span>Display Population Analysis Summary Statistics</span>
                </Button>
                {this.renderNotifications()}
            </div>
        )
    }
}

export default RefugeeModel
